import { motion } from 'framer-motion';
import {
  CheckCircle2,
  ArrowRight,
  Download,
  FileText,
  Filter,
  Search as SearchIcon
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { useState } from 'react';
import { Header } from '@/components/layout/Header';
import { FCRAFooterText } from '@/components/layout/FCRADisclaimer';
import { logger } from '@/utils/logger';
import { fadeInUp, staggerContainer } from '@/components/marketing/animations/marketingAnimations';
import { MarketingButton, MarketingCard, Chip, Pill } from '@/components/marketing/primitives';
import { OperationCard } from '@/components/marketing/products/OperationCard';
import { ComparisonMatrix } from '@/components/marketing/products/ComparisonMatrix';
import { ProductFAQs } from '@/components/marketing/products/ProductFAQs';

export default function ProductsPage() {
  const [activeTab, setActiveTab] = useState('validate');
  const [isDownloading, setIsDownloading] = useState(false);

  const downloadSampleCSV = () => {
    logger.info('Downloading CSV template from public assets');
    setIsDownloading(true);

    try {
      // Download the static CSV file from public directory
      // This file is automatically deployed with the app to Amplify
      const link = document.createElement('a');
      link.href = '/collectability-batch-template.csv';
      link.download = 'collectability-batch-template.csv';
      link.style.visibility = 'hidden';

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      logger.info('CSV template download initiated', {
        source: 'public_assets',
        fileName: 'collectability-batch-template.csv'
      });
    } finally {
      setIsDownloading(false);
    }
  };

  const tabContent = {
    validate: {
      specs: [
        'Primary contact verification',
        'Deliverability checks',
        'Activity-based ranking',
        'Secondary contact filtering'
      ],
      chips: [{ label: 'Batch', variant: 'default' as const }]
    },
    enrich: {
      specs: [
        'Public record skip tracing',
        'Multi-source contact discovery',
        'Address & phone appending',
        'Source attribution hints'
      ],
      chips: [{ label: 'Batch', variant: 'default' as const }]
    },
    validateEnrich: {
      specs: [
        'Skip trace + re-validation',
        'Identity & fraud scoring',
        'Invalid data filtering',
        'De-duplication pipeline'
      ],
      chips: [{ label: 'Batch', variant: 'default' as const }]
    },
    score: {
      specs: [
        'IntelligentDebt™ Algorithm',
        'Current spending signals',
        'Recovery probability bands',
        'AI action plan (optional)'
      ],
      chips: [
        { label: 'Batch & Web', variant: 'primary' as const },
        { label: 'AI', variant: 'default' as const }
      ]
    },
    scoreEnrich: {
      specs: [
        'Full enrichment + scoring',
        'Validated primary contacts',
        'Recovery recommendations',
        'Tailored outreach strategy'
      ],
      chips: [
        { label: 'Batch & Web', variant: 'primary' as const },
        { label: 'AI', variant: 'default' as const }
      ]
    }
  };

  const comparisonData = [
    {
      label: 'Contact quality',
      traditional: 'Exists? maybe.',
      collectability: 'Validated primaries ranked for reach.'
    },
    {
      label: 'Scoring signals',
      traditional: 'Static, credit-heavy, stale.',
      collectability: 'Current behavioral & demographic proxies.'
    },
    {
      label: 'Actionability',
      traditional: 'Manual cleanup.',
      collectability: 'Decision-ready with optional AI plan.'
    }
  ];

  const faqData = [
    {
      question: 'Is this FCRA compliant?',
      answer: 'No. Non-FCRA use cases only.'
    },
    {
      question: 'Can I run web searches in bulk?',
      answer: 'Yes, for debt collection. Use the batch uploader to process multiple searches at once with the same pricing and features as individual web searches.'
    },
    {
      question: 'What does the AI add-on do?',
      answer: 'Creates a tailored outreach plan based on the debtor\'s IntelligentDebt™ score, recovery band, and demographic signals. It suggests contact channels, timing cadence, and settlement strategies to maximize recovery probability.'
    },
    {
      question: 'How are primaries decided?',
      answer: 'Multi-source validation combined with activity and deliverability checks. We rank contacts based on recency, usage frequency, and verification across trusted data sources to identify the most reliable contact methods.'
    },
    {
      question: 'Can I get raw enrichment only?',
      answer: 'Yes; use Enrich or Validate+Enrich for a filtered set. Enrich provides all discovered contacts, while Validate+Enrich includes quality filtering and fraud scoring to help you focus on the most actionable data.'
    },
    {
      question: 'What makes IntelligentDebt™ different from credit scores?',
      answer: 'Traditional credit scores reflect past borrowing behavior and may be months or years old. IntelligentDebt™ uses current spending patterns, demographic proxies, and real-time behavioral signals to predict collection probability—giving you forward-looking intelligence instead of historical snapshots.'
    }
  ];

  return (
    <div className="min-h-screen bg-[#F7F9FC] font-['Inter',sans-serif]">
      {/* Header */}
      <Header />

      {/* Hero Section */}
      <section id="overview" className="bg-[#F7F9FC] pt-16 pb-12" data-testid="products-hero">
        <div className="mx-auto max-w-[1120px] px-5 md:px-8 py-12 md:py-10">
          <motion.div
            className="grid md:grid-cols-2 gap-10 items-start"
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
          >
            {/* Left Column */}
            <motion.div variants={fadeInUp}>
              <p className="text-xs uppercase tracking-wider text-[#2F7BFF] mb-3 font-semibold">
                PRODUCTS
              </p>
              <h1 className="text-4xl md:text-5xl font-semibold text-[#0E1325] tracking-tight leading-tight mb-5">
                Operations that make your data perform
              </h1>
              <p className="text-[15px] md:text-[16px] leading-7 text-[#5F6B7A] mb-6">
                Choose an operation, upload a CSV or run a web lookup, and get decision-ready outputs: primaries, fraud/identity signals, and IntelligentDebt™ scoring with optional AI action plans.
              </p>

              <div className="bg-white rounded-lg border border-[#E6ECF2] p-4 mb-6">
                <p className="text-sm text-[#5F6B7A] leading-relaxed">
                  <span className="font-semibold text-[#0E1325]">Non-FCRA Disclosure:</span> CollectABILITY is <span className="font-semibold text-[#0E1325]">not a Consumer Reporting Agency (CRA)</span> as defined by the Fair Credit Reporting Act (FCRA). Our services cannot be used for employment, credit, or tenant screening purposes. Web searches are designed for debt collection use cases. Batch processing is available across industries for permissible non-FCRA applications.
                </p>
              </div>

              {/* CTAs */}
              <div className="flex flex-wrap gap-4">
                <MarketingButton variant="primary" to="/batch" icon={ArrowRight}>
                  Try Batch
                </MarketingButton>
                <MarketingButton variant="outline" to="/search" icon={SearchIcon}>
                  Open Web Search
                </MarketingButton>
              </div>
            </motion.div>

            {/* Right Column - Operation Preview */}
            <motion.div variants={fadeInUp}>
              <MarketingCard className="p-4">
                <div className="mb-6">
                  <h3 className="text-sm font-semibold text-[#0E1325] mb-2 uppercase tracking-wider">
                    Operation Preview
                  </h3>
                  <p className="text-xs text-[#5F6B7A]">
                    Select an operation below to see what it does
                  </p>
                </div>

                <div className="flex gap-3">
                  {/* Left: Operation buttons stacked vertically */}
                  <div className="flex flex-col gap-2 min-w-[140px]">
                    {[
                      { id: 'validate', label: 'Validate' },
                      { id: 'enrich', label: 'Enrich' },
                      { id: 'validateEnrich', label: 'Validate+Enrich' },
                      { id: 'score', label: 'Score' },
                      { id: 'scoreEnrich', label: 'Score+Enrich' }
                    ].map((tab) => (
                      <button
                        key={tab.id}
                        onClick={() => setActiveTab(tab.id)}
                        className={`px-4 py-2.5 rounded-lg text-sm font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-[#2F7BFF]/40 text-left ${
                          activeTab === tab.id
                            ? 'bg-[#2F7BFF] text-white'
                            : 'bg-[#F7F9FC] text-[#5F6B7A] hover:bg-[#E6ECF2]'
                        }`}
                      >
                        {tab.label}
                      </button>
                    ))}
                  </div>

                  {/* Right: Preview content */}
                  <div className="flex-1">
                    <div className="space-y-3 mb-4">
                      {tabContent[activeTab as keyof typeof tabContent].specs.map((spec, i) => (
                        <div key={i} className="flex items-start gap-2 text-sm text-[#5F6B7A]">
                          <CheckCircle2 className="w-4 h-4 text-[#25C281] flex-shrink-0 mt-0.5" />
                          <span>{spec}</span>
                        </div>
                      ))}
                    </div>

                    <div className="flex gap-2 flex-wrap">
                      {tabContent[activeTab as keyof typeof tabContent].chips.map((chip, i) => (
                        <Chip key={i} variant={chip.variant}>
                          {chip.label}
                        </Chip>
                      ))}
                    </div>
                  </div>
                </div>
              </MarketingCard>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Operations Section */}
      <section id="operations" className="bg-white py-10 md:py-24" data-testid="operations-grid">
        <div className="mx-auto max-w-[1120px] px-5 md:px-8">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
          >
            <motion.div variants={fadeInUp} className="text-center mb-8">
              <h2 className="text-2xl md:text-3xl font-semibold text-[#0E1325] tracking-tight mb-4">
                What You Can Run
              </h2>
              <div className="bg-[#F7F9FC] border border-[#E6ECF2] rounded-lg p-4 max-w-3xl mx-auto">
                <p className="text-sm text-[#5F6B7A] leading-relaxed">
                  <span className="font-semibold text-[#0E1325]">Run modes:</span> Validate / Enrich / Validate+Enrich are Batch-only for any industry (non-FCRA). Score and Score+Enrich support Web (Debt Collection only, non-FCRA) or Batch.
                </p>
              </div>
            </motion.div>

            <div className="grid md:grid-cols-2 gap-3 mt-8 items-start">
              {/* Validate */}
              <motion.div variants={fadeInUp} className="h-full">
                <OperationCard
                  title="Validate"
                  chips={[{ label: 'Batch', variant: 'default' }]}
                  description="Is the contact information you have current, valid, and their primary info?"
                  outputs={[
                    'primary_phone',
                    'primary_email',
                    'deliverability_status',
                    'secondary_contacts'
                  ]}
                  bestFor="Cleaning existing lists and verifying contact quality before outreach campaigns."
                />
              </motion.div>

              {/* Enrich */}
              <motion.div variants={fadeInUp} className="h-full">
                <OperationCard
                  title="Enrich"
                  chips={[{ label: 'Batch', variant: 'default' }]}
                  description="Skip Tracing. Get more contact info and some other information to fill in the gaps."
                  outputs={[
                    'additional_phones',
                    'additional_emails',
                    'addresses',
                    'source_hints'
                  ]}
                  bestFor="Appending missing contact information from public records and trusted data sources."
                />
              </motion.div>

              {/* Validate + Enrich */}
              <motion.div variants={fadeInUp} className="h-full">
                <OperationCard
                  title="Validate + Enrich"
                  chips={[{ label: 'Batch', variant: 'default' }]}
                  description="After skip tracing, validate all the new info you received, filter out invalid and non-primary info and hone in on validated and useful contact information. Comes with identity and fraud scoring."
                  outputs={[
                    'primary_contacts',
                    'identity_match',
                    'fraud_score',
                    'invalid_filtered'
                  ]}
                  bestFor="Complete data enrichment with quality filtering and fraud detection for high-stakes collections."
                />
              </motion.div>

              {/* Score */}
              <motion.div variants={fadeInUp} className="h-full">
                <OperationCard
                  title="Score"
                  chips={[
                    { label: 'Batch & Web', variant: 'primary' },
                    { label: 'AI', variant: 'default' }
                  ]}
                  description="Uses the IntelligentDebt™ Algorithm to validate your contact info, then give you projected collections success with recovery options based on current spending habits and demographic data — not stale debt records or credit reports. The AI add-on provides a tailored contact & collections strategy."
                  outputs={[
                    'intelligentdebt_score',
                    'prob_recovery_band',
                    'recommended_channels',
                    'timing_cadence'
                  ]}
                  bestFor="Prioritizing collection efforts and maximizing recovery rates with AI-driven insights."
                />
              </motion.div>

              {/* Score + Enrich */}
              <motion.div variants={fadeInUp} className="md:col-span-2">
                <OperationCard
                  title="Score + Enrich"
                  chips={[
                    { label: 'Batch & Web', variant: 'primary' },
                    { label: 'AI', variant: 'default' }
                  ]}
                  description="Public record skip trace first, validate best info, then provide the IntelligentDebt™ scoring system with optional AI action plan."
                  outputs={[
                    'validated_primary_set',
                    'intelligentdebt_score',
                    'ai_action_plan (optional)',
                    'enrichment_sources',
                    'recovery_recommendations'
                  ]}
                  bestFor="End-to-end solution from data discovery to recovery strategy with full AI guidance."
                />
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Data Sources Section */}
      <section id="data-sources" className="bg-[#F7F9FC] py-10 md:py-24">
        <div className="mx-auto max-w-[1120px] px-5 md:px-8">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
          >
            <motion.div variants={fadeInUp} className="text-center mb-12">
              <h2 className="text-2xl md:text-3xl font-semibold text-[#0E1325] tracking-tight mb-3">
                What Powers Our Data
              </h2>
              <p className="text-[15px] text-[#5F6B7A] max-w-2xl mx-auto">
                Our operations leverage multiple data sources and proprietary algorithms to deliver accurate, actionable intelligence.
              </p>
            </motion.div>

            <div className="grid md:grid-cols-3 gap-3">
              <motion.div variants={fadeInUp}>
                <MarketingCard className="p-4 h-full">
                  <h3 className="text-lg font-semibold text-[#0E1325] mb-3">
                    Multiple Data Sources
                  </h3>
                  <p className="text-[15px] text-[#5F6B7A] leading-7">
                    Public records, utility connections, address history, phone registries, email verification services, and more. Cross-referenced for accuracy.
                  </p>
                </MarketingCard>
              </motion.div>

              <motion.div variants={fadeInUp}>
                <MarketingCard className="p-4 h-full">
                  <h3 className="text-lg font-semibold text-[#0E1325] mb-3">
                    Real-Time Validation
                  </h3>
                  <p className="text-[15px] text-[#5F6B7A] leading-7">
                    Active deliverability checks, phone line status verification, and recent activity signals to identify primary contact methods.
                  </p>
                </MarketingCard>
              </motion.div>

              <motion.div variants={fadeInUp}>
                <MarketingCard className="p-4 h-full">
                  <h3 className="text-lg font-semibold text-[#0E1325] mb-3">
                    IntelligentDebt™ Algorithm
                  </h3>
                  <p className="text-[15px] text-[#5F6B7A] leading-7">
                    Analyzes real-time spending patterns not reflected in traditional credit reports or debt records. Provides a measure of non-obligated liquidity—how much discretionary cash flow a debtor has available for settlements.
                  </p>
                </MarketingCard>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Pipeline Diagram */}
      <section id="pipeline" className="bg-white py-10 md:py-24" data-testid="pipeline-diagram">
        <div className="mx-auto max-w-[1120px] px-5 md:px-8">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
          >
            <motion.div variants={fadeInUp} className="text-center mb-12">
              <h2 className="text-2xl md:text-3xl font-semibold text-[#0E1325] tracking-tight mb-3">
                Processing Pipeline Example
              </h2>
              <p className="text-[15px] text-[#5F6B7A] max-w-2xl mx-auto">
                Score+Enrich with AI Add-On — our most comprehensive operation
              </p>
            </motion.div>

            <motion.div variants={fadeInUp}>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                <Pill
                  number="1"
                  title="Upload"
                  description="CSV upload with instant validation"
                />
                <Pill
                  number="2"
                  title="Enrich"
                  description="Public records & multi-source appending"
                />
                <Pill
                  number="3"
                  title="Re-validate"
                  description="Filter invalid, rank primaries"
                />
                <Pill
                  number="4"
                  title="Score"
                  description="IntelligentDebt™ + AI analysis"
                />
                <Pill
                  number="5"
                  title="Export"
                  description="Decision-ready CSV download"
                />
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Schema & Sample Files */}
      <section id="schemas" className="bg-[#F7F9FC] py-10 md:py-24">
        <div className="mx-auto max-w-[1120px] px-5 md:px-8">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
          >
            <motion.div variants={fadeInUp} className="text-center mb-12">
              <h2 className="text-2xl md:text-3xl font-semibold text-[#0E1325] tracking-tight mb-3">
                Input & Output Schemas
              </h2>
              <p className="text-[15px] text-[#5F6B7A] max-w-2xl mx-auto">
                Understanding the data format helps you prepare files and know what to expect from results.
              </p>
            </motion.div>

            <div className="grid md:grid-cols-2 gap-4">
              {/* Left - Input Schema */}
              <motion.div variants={fadeInUp}>
                <MarketingCard className="p-4 h-full">
                  <h3 className="text-lg font-semibold text-[#0E1325] mb-4 flex items-center gap-2">
                    <FileText className="w-5 h-5 text-[#2F7BFF]" />
                    Batch CSV Input Schema
                  </h3>
                  <div className="mb-4 p-3 bg-[#F7F9FC] rounded-lg border border-[#E6ECF2]">
                    <p className="text-xs text-[#5F6B7A]">
                      <span className="font-semibold text-[#0E1325]">Required:</span> first_name, last_name, and <span className="font-semibold">at least ONE</span> of: address, phone, or email. Two or more contact points improve match accuracy.
                    </p>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-[#E6ECF2]">
                          <th className="text-left py-2 px-3 font-semibold text-[#0E1325]">Column</th>
                          <th className="text-left py-2 px-3 font-semibold text-[#0E1325]">Required</th>
                        </tr>
                      </thead>
                      <tbody className="text-[#5F6B7A]">
                        {[
                          { col: 'first_name', req: 'Yes' },
                          { col: 'last_name', req: 'Yes' },
                          { col: 'address', req: 'One of' },
                          { col: 'city', req: 'If address' },
                          { col: 'state', req: 'If address' },
                          { col: 'zip', req: 'If address' },
                          { col: 'phone', req: 'One of' },
                          { col: 'email', req: 'One of' },
                          { col: 'debt_amount', req: 'For scoring' }
                        ].map((row, i) => (
                          <tr key={i} className="border-b border-[#E6ECF2] last:border-0">
                            <td className="py-2 px-3 font-mono text-xs">{row.col}</td>
                            <td className="py-2 px-3">
                              {row.req === 'Yes' ? (
                                <span className="text-[#FF6B6B] font-medium">{row.req}</span>
                              ) : (
                                <span className="text-[#5F6B7A]">{row.req}</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <div className="mt-6">
                    <button
                      onClick={downloadSampleCSV}
                      disabled={isDownloading}
                      className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#25C281] text-white hover:bg-[#20A86D] transition-colors font-medium text-sm focus:outline-none focus:ring-2 focus:ring-[#25C281]/40 disabled:opacity-50 disabled:cursor-not-allowed"
                      data-testid="schema-download"
                    >
                      <Download className="w-4 h-4" />
                      {isDownloading ? 'Downloading...' : 'Download CSV Template'}
                    </button>
                  </div>
                </MarketingCard>
              </motion.div>

              {/* Right - Output Columns */}
              <motion.div variants={fadeInUp}>
                <MarketingCard className="p-4 h-full">
                  <h3 className="text-lg font-semibold text-[#0E1325] mb-4 flex items-center gap-2">
                    <Filter className="w-5 h-5 text-[#2F7BFF]" />
                    Output Columns
                  </h3>
                  <div className="mb-4 p-3 bg-[#FFF7ED] rounded-lg border border-[#FFA94D]">
                    <p className="text-xs text-[#8B4513]">
                      <span className="font-semibold">Important:</span> Output columns <span className="font-semibold">vary significantly by operation</span>. Below are sample columns that may be returned.
                    </p>
                  </div>
                  <ul className="space-y-2 text-sm text-[#5F6B7A]">
                    {[
                      'primary_phone',
                      'primary_email',
                      'deliverability_status',
                      'fraud_score',
                      'identity_match',
                      'enrichment_sources',
                      'intelligentdebt_score',
                      'score_reason_codes',
                      'recovery_probability'
                    ].map((col, i) => (
                      <li key={i} className="flex items-start gap-2">
                        <span className="w-1.5 h-1.5 rounded-full bg-[#2F7BFF] mt-2 flex-shrink-0"></span>
                        <span className="font-mono text-xs">{col}</span>
                      </li>
                    ))}
                  </ul>
                </MarketingCard>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Why CollectABILITY vs Traditional */}
      <section id="comparison" className="bg-white py-10 md:py-24">
        <div className="mx-auto max-w-[1120px] px-5 md:px-8">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
          >
            <motion.div variants={fadeInUp} className="text-center mb-12">
              <h2 className="text-2xl md:text-3xl font-semibold text-[#0E1325] tracking-tight mb-3">
                How We Compare to Traditional Solutions
              </h2>
              <p className="text-[15px] text-[#5F6B7A] max-w-2xl mx-auto">
                Our approach focuses on validated, actionable data rather than raw data dumps.
              </p>
            </motion.div>

            <motion.div variants={fadeInUp}>
              <ComparisonMatrix data={comparisonData} />
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* FAQs */}
      <section id="faqs" className="bg-[#F7F9FC] py-10 md:py-24" data-testid="faqs">
        <div className="mx-auto max-w-[1120px] px-5 md:px-8">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
          >
            <motion.div variants={fadeInUp} className="text-center mb-12">
              <h2 className="text-2xl md:text-3xl font-semibold text-[#0E1325] tracking-tight">
                Frequently Asked Questions
              </h2>
            </motion.div>

            <motion.div variants={fadeInUp} className="max-w-3xl mx-auto">
              <ProductFAQs faqs={faqData} />
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* CTA Band */}
      <section className="bg-[#0B1020] py-10 md:py-24">
        <div className="mx-auto max-w-[1120px] px-5 md:px-8">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
            className="text-center"
          >
            <h2 className="text-2xl md:text-3xl font-semibold text-white tracking-tight mb-4">
              Ready to run your first operation?
            </h2>
            <p className="text-[15px] md:text-[16px] text-[#9CA3AF] mb-8 max-w-2xl mx-auto">
              Choose batch processing for high-volume workflows or web search for instant individual lookups. No subscription required.
            </p>
            <div className="flex flex-wrap gap-4 justify-center">
              <MarketingButton variant="dark" to="/batch">
                Try Batch
              </MarketingButton>
              <MarketingButton variant="dark" to="/search">
                Open Web Search
              </MarketingButton>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#0B1020] py-12">
        <div className="mx-auto max-w-[1120px] px-5 md:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
            <div className="col-span-1 md:col-span-2">
              <Link to="/" className="flex items-center mb-4" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
                <img
                  src="/linear-logo-sm.png"
                  alt="CollectABILITY"
                  className="h-10"
                  loading="lazy"
                  height="40"
                />
              </Link>
              <p className="text-[#9CA3AF] text-sm mb-4 max-w-md">
                AI-powered debt collection intelligence platform for smarter collections and higher returns.
              </p>
              <p className="text-[#9CA3AF] text-sm">
                support@collectability.ai
              </p>
            </div>

            <div>
              <h3 className="text-white font-semibold mb-4">Product</h3>
              <ul className="space-y-2">
                <li>
                  <Link to="/batch" className="text-[#9CA3AF] hover:text-white text-sm transition-colors" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
                    Batch Processing
                  </Link>
                </li>
                <li>
                  <Link to="/search" className="text-[#9CA3AF] hover:text-white text-sm transition-colors" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
                    Web Search
                  </Link>
                </li>
                <li>
                  <span className="text-[#9CA3AF] text-sm opacity-50">
                    Features
                  </span>
                </li>
              </ul>
            </div>

            <div>
              <h3 className="text-white font-semibold mb-4">Company</h3>
              <ul className="space-y-2">
                <li>
                  <Link to="/contact" className="text-[#9CA3AF] hover:text-white text-sm transition-colors" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
                    Contact Us
                  </Link>
                </li>
                <li>
                  <Link to="/terms" className="text-[#9CA3AF] hover:text-white text-sm transition-colors" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
                    Terms of Use
                  </Link>
                </li>
                <li>
                  <Link to="/privacy" className="text-[#9CA3AF] hover:text-white text-sm transition-colors" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
                    Privacy Policy
                  </Link>
                </li>
                <li>
                  <Link to="/account" className="text-[#9CA3AF] hover:text-white text-sm transition-colors" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
                    My Account
                  </Link>
                </li>
              </ul>
            </div>
          </div>

          <div className="border-t border-[#1F2937] pt-8 space-y-3">
            <FCRAFooterText />
            <p className="text-[#9CA3AF] text-sm text-center">
              &copy; 2024 CollectABILITY, LLC. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
